import React, { useState, useEffect, useRef } from 'react';
import { deleteField } from 'firebase/firestore';
import { useSession } from '../context/SessionContext';
import { useProfile } from '../context/ProfileContext';
import { logWorkoutSession, updateLogSet, deleteLogSet } from '../services/logsService';
import { createRoutine, updateRoutine } from '../services/routinesService';
import { fetchPreviousSessionSets, getExerciseMax } from '../firestoreUtils';
import { isCardioExercise } from '../constants/exerciseTaxonomy';
import { formatLogAmount, formatCardioDurationShort } from '../utils/formatLogAmount';
import AmountInput from './AmountInput';
import IconButton from './IconButton';
import Modal from './Modal';
import ExerciseSelectorModal from './ExerciseSelectorModal';
import ExerciseHistoryModal from './ExerciseHistoryModal';
import ExerciseNoteModal from './ExerciseNoteModal';
import ConfirmModal from './ConfirmModal';
import { dayKeyFor } from '../utils/groupLogs';
import { fetchExerciseNote } from '../services/exerciseNotesService';
import './comic.css';

// A fresh, unlogged row for one set/session -- shape depends on whether the
// exercise is Cardio (hours/minutes/distance) or strength (weight/reps).
// Shared by the initial per-mission row seeding, "+ Add Set", and adding an
// exercise on the fly, so all three ways a row can be created stay
// byte-identical in shape.
const makeEmptyRow = (cardio) =>
  cardio
    ? { hours: "", minutes: "", distance: "", durationMinutes: null, logged: false, id: null }
    : { weight: "", reps: "", logged: false, id: null, isPR: false };

// "Today" for note-keying purposes -- a note written mid-mission is always
// keyed to the calendar day the mission is actually happening on, same as a
// freshly-logged set would bucket into "Previously..." (both use "right
// now", not the mission's original start time, consistent with that view).
const todayKey = () => dayKeyFor(new Date());

// exerciseLibrary (official + this account's custom exercises, already
// merged/loaded by WorkoutLog) backs the "add an exercise on the fly" picker.
// profileId is required -- RoutinePicker mounts one ActiveMission per
// profile that has a live session (see SessionContext.jsx), so each
// instance always knows which Hero's mission it is, regardless of which
// Hero is currently focused in ProfileSwitcher.
// lockCancelConfirm: true only while GuidedTour.jsx is actively waiting for
// the real user to tap this exact Discard button as part of the "Cancelling
// a Mission"/"Cleaning Up" tour steps -- disables "Keep Going" (and blocks
// the Modal's own X/backdrop/Escape dismissal) so a demo Mission the tour
// promised to clean up can't be left stranded by backing out of the
// confirmation instead. False in every normal, non-tour use of this dialog.
const ActiveMission = ({ userId, profileId, exerciseLibrary = [], lockCancelConfirm = false }) => {
  const { getSession, endSession } = useSession();
  const { profiles } = useProfile();
  const activeSession = getSession(profileId);
  // This instance's own Hero -- NOT necessarily whichever profile is
  // currently focused elsewhere in the app (another profile's ActiveMission
  // may be the one visible right now).
  const profile = profiles.find((p) => p.id === profileId);

  // Per-exercise set rows, keyed by exerciseId:
  // { [exerciseId]: [{ weight, reps, logged, id, isPR }, ...] }
  // Row count starts at the routine's configured sets-per-exercise (falls
  // back to 3 for routines saved before that field existed) and can grow via
  // "+ Add Set". Each row IS both the input and, once logged, the record --
  // no separate pending-input + logged-list split like the old incremental
  // "type then click Log Set" flow.
  const [rows, setRows] = useState({});
  // Last workout's sets per exercise: { [exerciseId]: [{ weight, reps }, ...] }
  const [previousSets, setPreviousSets] = useState({});
  // Flip to true when "End Mission" is clicked; shows the debrief before the
  // session is actually cleared via endSession().
  const [showDebrief, setShowDebrief] = useState(false);
  // In-panel dialogue bubble standing in for blocking alert()s on local
  // validation/transmission errors: { exerciseId, message }. Auto-dismisses.
  const [bubble, setBubble] = useState(null);
  // Which row is mid-save, as `${exerciseId}:${index}`, so only that row's
  // checkmark disables while a write is in flight.
  const [savingRow, setSavingRow] = useState(null);
  // "Cancel Mission" (abandon this in-progress workout) confirmation state.
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Exercises added mid-mission that weren't part of the original routine --
  // rendered alongside `activeSession.exercises` but tracked separately so
  // we know, at Mission Complete, whether to ask what to do with the
  // routine (this and/or removedExercises being non-empty is what triggers
  // that prompt).
  const [addedExercises, setAddedExercises] = useState([]);
  const [showAddExercise, setShowAddExercise] = useState(false);
  // Which exercise's "i" history popup is open: { exerciseId, name } or null.
  const [historyExercise, setHistoryExercise] = useState(null);
  // Which exercise's note popup is open: { exerciseId, name } or null.
  const [noteExercise, setNoteExercise] = useState(null);
  // Today's note text per exercise, keyed by exerciseId -- only drives the
  // note icon's "has a note" color, the modal itself always re-fetches fresh
  // on open. { [exerciseId]: string }
  const [todayNotes, setTodayNotes] = useState({});
  // Exercise pending "Remove from mission?" confirmation (the exercise
  // object, or null), plus whether that confirmed removal is in flight.
  const [confirmRemoveExercise, setConfirmRemoveExercise] = useState(null);
  const [removingExercise, setRemovingExercise] = useState(false);
  // Original-routine exercises removed mid-mission (the mirror image of
  // addedExercises). An exercise added THIS session that's then removed
  // again just gets un-added (see handleRemoveExercise) -- it never lands
  // here, since there's no routine relationship to track for it.
  const [removedExercises, setRemovedExercises] = useState([]);
  // Shown instead of jumping straight to the debrief when addedExercises or
  // removedExercises is non-empty: lets the user fold the change(s) into the
  // existing routine, spin off a new routine, or just leave the routine
  // definition untouched (the logged sets are already saved either way).
  const [showRoutineChoice, setShowRoutineChoice] = useState(false);
  const [savingRoutineChoice, setSavingRoutineChoice] = useState(false);
  const [routineChoiceError, setRoutineChoiceError] = useState(null);

  // Live training-order override for the exercise cards, as an ordered array
  // of exerciseIds -- the single source of truth for display order once a
  // mission starts (initialized from the routine's saved order below, kept
  // in sync as exercises are added/removed mid-mission). Separate from
  // addedExercises/removedExercises since reordering doesn't change which
  // exercises are present, only their sequence.
  const [exerciseOrder, setExerciseOrder] = useState([]);
  // Flips true the first time a drag actually moves a card to a new
  // position -- alongside addedExercises/removedExercises, this is what
  // triggers the Mission Complete routine-choice prompt.
  const [orderChanged, setOrderChanged] = useState(false);

  // Press-and-drag reordering for the exercise cards, via the native
  // Pointer Events API -- same technique RoutinePicker.jsx's "Mission
  // order" list uses (unifies mouse/touch/pen, no drag-and-drop library).
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const dragStartY = useRef(0);
  const cardRefs = useRef([]);

  useEffect(() => {
    if (!bubble) return undefined;
    const timer = setTimeout(() => setBubble(null), 3200);
    return () => clearTimeout(timer);
  }, [bubble]);

  // Initialize each exercise's row set once per mission (new activeSession
  // reference -- SessionContext only creates one via startSession, never on
  // re-render, so this fires exactly once per Mission).
  useEffect(() => {
    if (!activeSession || !activeSession.exercises) return;
    const initial = {};
    activeSession.exercises.forEach((exercise) => {
      const cardio = isCardioExercise(exercise);
      // A Cardio exercise is always exactly one row -- there's no "3 sets
      // of treadmill" in the way there's "3 sets of bench press" -- per
      // direct user request, regardless of whatever `sets` count the
      // routine happens to carry for it.
      const count = cardio ? 1 : (exercise.sets || 3);
      initial[exercise.exerciseId] = Array.from({ length: count }, () => makeEmptyRow(cardio));
    });
    setRows(initial);
    setExerciseOrder(
      [...activeSession.exercises].sort((a, b) => a.order - b.order).map((ex) => ex.exerciseId)
    );
  }, [activeSession]);

  // Load each exercise's previous session sets and prefill rows that are
  // still completely empty -- never overwrites anything the user already
  // typed or already logged.
  useEffect(() => {
    if (!activeSession || !activeSession.exercises || !userId) return;

    let cancelled = false;
    const missionStartTime = new Date(activeSession.startTime);

    const loadPrevious = async () => {
      const results = await Promise.all(
        activeSession.exercises.map(async (exercise) => {
          const sets = await fetchPreviousSessionSets(userId, profileId, exercise.exerciseId, missionStartTime);
          return [exercise.exerciseId, sets];
        })
      );
      if (cancelled) return;

      const byId = Object.fromEntries(results);
      setPreviousSets(byId);

      const cardioByExerciseId = new Map(
        activeSession.exercises.map((ex) => [ex.exerciseId, isCardioExercise(ex)])
      );

      setRows((prev) => {
        const next = { ...prev };
        for (const [exerciseId, sets] of results) {
          const exRows = next[exerciseId];
          if (!exRows || sets.length === 0) continue;
          const cardio = cardioByExerciseId.get(exerciseId);
          next[exerciseId] = exRows.map((row, i) => {
            if (row.logged) return row;
            const hist = sets[i] ?? sets[sets.length - 1];
            if (!hist) return row;
            if (cardio) {
              if (row.hours || row.minutes || row.distance) return row;
              const total = Math.max(0, Math.round(Number(hist.durationMinutes) || 0));
              const h = Math.floor(total / 60);
              const m = total % 60;
              return {
                ...row,
                hours: h ? String(h) : "",
                minutes: m ? String(m) : "",
                distance: hist.distance != null ? String(hist.distance) : ""
              };
            }
            if (row.weight || row.reps) return row;
            return { ...row, weight: String(hist.weight), reps: String(hist.reps) };
          });
        }
        return next;
      });
    };

    loadPrevious();
    return () => {
      cancelled = true;
    };
  }, [activeSession, userId, profileId]);

  // Load today's note (if any) per exercise, purely to color the note icon
  // -- the note popup itself always fetches fresh on open regardless.
  useEffect(() => {
    if (!activeSession || !activeSession.exercises || !userId) return undefined;

    let cancelled = false;
    const key = todayKey();

    Promise.all(
      activeSession.exercises.map(async (exercise) => {
        const note = await fetchExerciseNote(userId, profileId, exercise.exerciseId, key);
        return [exercise.exerciseId, note];
      })
    ).then((results) => {
      if (cancelled) return;
      setTodayNotes(Object.fromEntries(results));
    });

    return () => {
      cancelled = true;
    };
  }, [activeSession, userId, profileId]);

  // Nothing renders unless a mission is live.
  if (!activeSession) return null;

  // Combines the routine's originally-planned exercises (minus any removed
  // mid-mission) with any added mid-mission, then sequenced by
  // `exerciseOrder` -- the live, drag-reorderable training order.
  // `addedExercises`/`removedExercises`/`orderChanged` together decide, at
  // Mission Complete, whether to prompt about updating the routine (plain
  // useState/consts, not useMemo -- these lists are small and this only
  // needs to be correct, not micro-optimized).
  const removedExerciseIds = new Set(removedExercises.map((ex) => ex.exerciseId));
  const exercisesById = new Map(
    [
      ...activeSession.exercises.filter((ex) => !removedExerciseIds.has(ex.exerciseId)),
      ...addedExercises
    ].map((ex) => [ex.exerciseId, ex])
  );
  const allExercises = exerciseOrder.map((id) => exercisesById.get(id)).filter(Boolean);

  // Exercise-library entries not already in this mission, for the
  // "+ Add Exercise" picker.
  const includedExerciseIds = new Set(allExercises.map((ex) => ex.exerciseId));
  const availableExercises = exerciseLibrary.filter((ex) => !includedExerciseIds.has(ex.id));

  const updateRow = (exerciseId, index, patch) => {
    setRows((prev) => {
      const arr = [...(prev[exerciseId] || [])];
      if (!arr[index]) return prev;
      arr[index] = { ...arr[index], ...patch };
      return { ...prev, [exerciseId]: arr };
    });
  };

  const handleFieldChange = (exerciseId, index, field, value) => {
    updateRow(exerciseId, index, { [field]: value });
  };

  // A logged row's inputs stay live-editable; blurring after a change saves
  // the correction in place, recomputing isPR against the exercise's max
  // excluding this set itself (same approach as the old dedicated edit UI).
  // Cardio rows skip the PR recompute entirely -- isPR only ever applies to
  // strength sets.
  const handleFieldBlur = async (exercise, index) => {
    const row = rows[exercise.exerciseId]?.[index];
    if (!row || !row.logged) return;
    const cardio = isCardioExercise(exercise);

    if (cardio) {
      const totalMinutes = (Number(row.hours) || 0) * 60 + (Number(row.minutes) || 0);
      if (totalMinutes <= 0) return;
      const rowKey = `${exercise.exerciseId}:${index}`;
      setSavingRow(rowKey);
      try {
        const distanceNum = row.distance ? Number(row.distance) : null;
        const result = await updateLogSet(row.id, {
          durationMinutes: totalMinutes,
          distance: distanceNum !== null ? distanceNum : deleteField()
        });
        if (!result.success) {
          setBubble({ exerciseId: exercise.exerciseId, message: "Couldn't update that session. Try again." });
          return;
        }
        updateRow(exercise.exerciseId, index, { durationMinutes: totalMinutes });
      } finally {
        setSavingRow(null);
      }
      return;
    }

    // Weight is optional (bodyweight exercises -- push-ups, pull-ups, etc.);
    // Number("") is 0, so an empty weight correctly logs as 0 rather than
    // being rejected. Reps stays required -- a set with no rep count isn't
    // a meaningful record.
    if (!row.reps) return;

    const rowKey = `${exercise.exerciseId}:${index}`;
    setSavingRow(rowKey);
    try {
      const weightNum = Number(row.weight);
      const repsNum = Number(row.reps);
      const maxExcludingThis = await getExerciseMax(userId, profileId, exercise.exerciseId, row.id);
      const isPR = weightNum > maxExcludingThis;
      const result = await updateLogSet(row.id, { weight: weightNum, reps: repsNum, isPR });
      if (!result.success) {
        setBubble({ exerciseId: exercise.exerciseId, message: "Couldn't update that set. Try again." });
        return;
      }
      updateRow(exercise.exerciseId, index, { isPR });
    } finally {
      setSavingRow(null);
    }
  };

  // Tapping an unlogged row's checkmark logs it.
  const handleLogRow = async (exercise, index) => {
    const row = rows[exercise.exerciseId]?.[index];
    if (!row || row.logged) return;
    const cardio = isCardioExercise(exercise);
    const rowKey = `${exercise.exerciseId}:${index}`;
    setSavingRow(rowKey);

    try {
      if (cardio) {
        const totalMinutes = (Number(row.hours) || 0) * 60 + (Number(row.minutes) || 0);
        if (totalMinutes <= 0) {
          setBubble({ exerciseId: exercise.exerciseId, message: "Log a duration, Hero." });
          return;
        }
        const distanceNum = row.distance ? Number(row.distance) : undefined;

        const payload = {
          exerciseId: exercise.exerciseId,
          profileId,
          ownerUserId: userId,
          durationMinutes: totalMinutes,
          routineName: activeSession.routineName,
          note: `${activeSession.routineName} - ${exercise.name}`
        };
        if (distanceNum !== undefined) payload.distance = distanceNum;

        const result = await logWorkoutSession(payload);
        if (!result.success) {
          setBubble({ exerciseId: exercise.exerciseId, message: "Transmission failed. Session not logged." });
          return;
        }

        setRows((prev) => {
          const arr = [...(prev[exercise.exerciseId] || [])];
          arr[index] = { ...arr[index], logged: true, id: result.id, durationMinutes: totalMinutes };
          // Same "cascade into a still-empty next row" convenience as the
          // strength path below, carrying hours/minutes/distance forward
          // instead of weight/reps.
          const nextIndex = index + 1;
          if (arr[nextIndex] && !arr[nextIndex].logged && !arr[nextIndex].hours && !arr[nextIndex].minutes && !arr[nextIndex].distance) {
            arr[nextIndex] = { ...arr[nextIndex], hours: row.hours, minutes: row.minutes, distance: row.distance };
          }
          return { ...prev, [exercise.exerciseId]: arr };
        });
        return;
      }

      // Weight is optional -- Number("") is 0, so a blank weight field
      // correctly logs as a 0-weight (bodyweight) set rather than blocking.
      if (!row.reps) {
        setBubble({ exerciseId: exercise.exerciseId, message: "Log your reps, Hero." });
        return;
      }

      const weightNum = Number(row.weight);
      const repsNum = Number(row.reps);
      // Re-query the historical max fresh each time so back-to-back heavier
      // sets in the same mission correctly see the prior (already-written)
      // set as the max to beat.
      const currentMax = await getExerciseMax(userId, profileId, exercise.exerciseId);
      const isPR = weightNum > currentMax;

      const result = await logWorkoutSession({
        exerciseId: exercise.exerciseId,
        profileId,
        ownerUserId: userId,
        weight: weightNum,
        reps: repsNum,
        isPR,
        routineName: activeSession.routineName,
        note: `${activeSession.routineName} - ${exercise.name}`
      });

      if (!result.success) {
        setBubble({ exerciseId: exercise.exerciseId, message: "Transmission failed. Set not logged." });
        return;
      }

      setRows((prev) => {
        const arr = [...(prev[exercise.exerciseId] || [])];
        arr[index] = { ...arr[index], logged: true, id: result.id, isPR };
        // Cascade: if the next row is still completely empty (no history
        // prefilled it either), carry this set's numbers forward. Covers
        // "doing this exercise for the first time ever" -- when history
        // exists, that row was already prefilled above and this no-ops.
        const nextIndex = index + 1;
        if (arr[nextIndex] && !arr[nextIndex].logged && !arr[nextIndex].weight && !arr[nextIndex].reps) {
          arr[nextIndex] = { ...arr[nextIndex], weight: String(weightNum), reps: String(repsNum) };
        }
        return { ...prev, [exercise.exerciseId]: arr };
      });
    } finally {
      setSavingRow(null);
    }
  };

  // Un-logs an already-logged row. Tapping the same checkmark a second time
  // triggers this -- per direct user request, no separate delete icon next
  // to a logged row (that had briefly replaced this exact gesture, but was
  // reverted: "don't add an extra button... if they want to uncheck it,
  // they simply tap the check again").
  const handleDeleteRow = async (exercise, index) => {
    const row = rows[exercise.exerciseId]?.[index];
    if (!row || !row.logged) return;
    const rowKey = `${exercise.exerciseId}:${index}`;
    setSavingRow(rowKey);
    try {
      const result = await deleteLogSet(row.id);
      if (!result.success) {
        setBubble({ exerciseId: exercise.exerciseId, message: "Couldn't remove that set. Try again." });
        return;
      }
      updateRow(exercise.exerciseId, index, { logged: false, id: null, isPR: false });
    } finally {
      setSavingRow(null);
    }
  };

  // "Cancel Mission": abandon the whole in-progress workout, deleting every
  // set logged so far this session (client-side, from `rows` -- every
  // logged row already carries its own Firestore doc id, so this needs no
  // extra query) and clearing the session without showing the debrief.
  // Deliberately leaves the "SESSION_START" marker doc RoutinePicker wrote
  // when the mission began untouched: that marker is never cleaned up even
  // after a normal completed mission either (it's permanent time-boundary
  // bookkeeping for `fetchPreviousSessionSets`, filtered out of every
  // visible log list), so leaving it behind on cancel is consistent with
  // existing behavior, not a gap.
  const handleCancelMission = async () => {
    setCancelling(true);
    const idsToDelete = Object.values(rows)
      .flat()
      .filter((row) => row.logged && row.id)
      .map((row) => row.id);
    await Promise.all(idsToDelete.map((id) => deleteLogSet(id)));
    setCancelling(false);
    endSession(profileId);
  };

  const handleAddSet = (exercise) => {
    const exerciseId = exercise.exerciseId;
    const cardio = isCardioExercise(exercise);
    setRows((prev) => {
      const arr = prev[exerciseId] || [];
      const last = arr[arr.length - 1];
      const fresh = makeEmptyRow(cardio);
      // Carry the previous row's values forward as a starting point, same
      // convenience as the cascade-on-log behavior above.
      const seeded = last
        ? (cardio
          ? { ...fresh, hours: last.hours, minutes: last.minutes, distance: last.distance }
          : { ...fresh, weight: last.weight, reps: last.reps })
        : fresh;
      return { ...prev, [exerciseId]: [...arr, seeded] };
    });
  };

  // Adds an exercise mid-mission that wasn't part of the original routine.
  // Seeds its row set the same way the initial-load effect does (3 empty
  // rows) and, same as that effect, best-effort prefills them from history
  // if this exercise has been logged in a prior session -- so an on-the-fly
  // addition behaves identically to a pre-planned one from here on.
  //
  // Takes the full exercise doc (as returned by ExerciseSelectorModal's
  // onSelect for both an existing pick and a freshly-created exercise)
  // rather than just an id + a lookup into exerciseLibrary -- a freshly
  // created exercise isn't guaranteed to have propagated back into this
  // component's exerciseLibrary prop (loaded by a parent's onSnapshot
  // listener) by the moment onSelect fires, so looking it up here would be
  // a real race condition; the modal already hands back everything needed.
  const handleAddExerciseOnTheFly = async (exerciseDoc) => {
    if (!exerciseDoc) return;

    const cardio = isCardioExercise(exerciseDoc);
    // Same "always exactly one row" rule as the initial per-mission seeding
    // above -- an on-the-fly addition behaves identically either way.
    const rowCount = cardio ? 1 : 3;

    const newExercise = {
      exerciseId: exerciseDoc.id,
      name: exerciseDoc.name,
      targetMuscle: exerciseDoc.targetMuscle,
      mainMuscle: exerciseDoc.mainMuscle,
      order: allExercises.length,
      sets: rowCount
    };

    setAddedExercises((prev) => [...prev, newExercise]);
    setExerciseOrder((prev) => [...prev, newExercise.exerciseId]);
    setRows((prev) => ({
      ...prev,
      [newExercise.exerciseId]: Array.from({ length: rowCount }, () => makeEmptyRow(cardio))
    }));
    setShowAddExercise(false);

    if (userId) {
      const missionStartTime = new Date(activeSession.startTime);
      fetchExerciseNote(userId, profileId, newExercise.exerciseId, todayKey()).then((note) => {
        setTodayNotes((prev) => ({ ...prev, [newExercise.exerciseId]: note }));
      });

      const sets = await fetchPreviousSessionSets(userId, profileId, newExercise.exerciseId, missionStartTime);
      if (sets.length === 0) return;
      setPreviousSets((prev) => ({ ...prev, [newExercise.exerciseId]: sets }));
      setRows((prev) => {
        const exRows = prev[newExercise.exerciseId];
        if (!exRows) return prev;
        const arr = exRows.map((row, i) => {
          const hist = sets[i] ?? sets[sets.length - 1];
          if (!hist) return row;
          if (cardio) {
            const total = Math.max(0, Math.round(Number(hist.durationMinutes) || 0));
            const h = Math.floor(total / 60);
            const m = total % 60;
            return {
              ...row,
              hours: h ? String(h) : "",
              minutes: m ? String(m) : "",
              distance: hist.distance != null ? String(hist.distance) : ""
            };
          }
          return { ...row, weight: String(hist.weight), reps: String(hist.reps) };
        });
        return { ...prev, [newExercise.exerciseId]: arr };
      });
    }
  };

  // Removes an exercise from the live mission -- now behind an explicit
  // confirm step (see confirmRemoveExercise below), per the 2026-10-12
  // app-wide decision that every delete-icon tap confirms first; this used
  // to be immediate/low-friction (mirroring "+ Add Exercise"'s own
  // no-confirmation feel), but that's superseded by the newer, broader
  // rule. Any sets already logged for it this session are
  // deleted along with it, same reasoning as Cancel Mission: removing the
  // exercise means "this isn't part of the workout," not "keep the data but
  // hide it." If it was added THIS session (in addedExercises, not the
  // original routine), it's just un-added -- there's no routine
  // relationship to track, so it never touches removedExercises.
  const handleRemoveExercise = async (exercise) => {
    const idsToDelete = (rows[exercise.exerciseId] || [])
      .filter((row) => row.logged && row.id)
      .map((row) => row.id);
    await Promise.all(idsToDelete.map((id) => deleteLogSet(id)));

    setRows((prev) => {
      const next = { ...prev };
      delete next[exercise.exerciseId];
      return next;
    });
    setPreviousSets((prev) => {
      const next = { ...prev };
      delete next[exercise.exerciseId];
      return next;
    });

    const wasAddedThisSession = addedExercises.some((ex) => ex.exerciseId === exercise.exerciseId);
    if (wasAddedThisSession) {
      setAddedExercises((prev) => prev.filter((ex) => ex.exerciseId !== exercise.exerciseId));
    } else {
      setRemovedExercises((prev) => [...prev, exercise]);
    }
    setExerciseOrder((prev) => prev.filter((id) => id !== exercise.exerciseId));
  };

  const confirmAndRemoveExercise = async () => {
    if (!confirmRemoveExercise || removingExercise) return;
    setRemovingExercise(true);
    await handleRemoveExercise(confirmRemoveExercise);
    setRemovingExercise(false);
    setConfirmRemoveExercise(null);
  };

  // Press-and-drag reordering for the exercise cards -- same Pointer Events
  // technique as RoutinePicker.jsx's "Mission order" list. dragIndex is the
  // card being lifted; dragOverIndex is whichever card the pointer is
  // currently over (the drop target if released now); dragOffsetY drives
  // the lifted card's live-follow transform.
  const handleDragPointerDown = (e, index) => {
    // Only the primary button/touch/pen contact starts a drag.
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStartY.current = e.clientY;
    setDragIndex(index);
    setDragOverIndex(index);
    setDragOffsetY(0);
  };

  const handleDragPointerMove = (e) => {
    if (dragIndex === null) return;
    setDragOffsetY(e.clientY - dragStartY.current);

    // Find which card the pointer is currently over, by comparing against
    // each card's live bounding box (not just index math), so the drop
    // target tracks accurately regardless of card height. Skip the dragged
    // card itself -- its own bounding box follows the pointer via the
    // translateY transform, so it would always "win" the hit-test against
    // itself before the loop ever reached the real target card.
    for (let i = 0; i < allExercises.length; i++) {
      if (i === dragIndex) continue;
      const el = cardRefs.current[i];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
        if (i !== dragOverIndex) setDragOverIndex(i);
        break;
      }
    }
  };

  const endDrag = () => {
    if (dragIndex !== null && dragOverIndex !== null && dragOverIndex !== dragIndex) {
      setExerciseOrder((prev) => {
        const next = [...prev];
        const [moved] = next.splice(dragIndex, 1);
        next.splice(dragOverIndex, 0, moved);
        return next;
      });
      setOrderChanged(true);
    }
    setDragIndex(null);
    setDragOverIndex(null);
    setDragOffsetY(0);
  };

  // The final {exerciseId, order, sets} list to write back to the routine,
  // derived straight from `exerciseOrder` -- the live, drag-reorderable
  // training order -- so it correctly reflects the current sequence
  // regardless of whether each exercise was original or added this session.
  // Each `sets` reflects however many rows it ended up with by the end of
  // the mission (including any extra "+ Add Set" taps), not the row-count
  // it started with.
  const buildMergedExercises = () =>
    exerciseOrder.map((exerciseId, index) => {
      const original = activeSession.exercises.find((ex) => ex.exerciseId === exerciseId);
      return {
        exerciseId,
        order: index,
        sets: rows[exerciseId]?.length || original?.sets || 3
      };
    });

  // Advances from the routine-choice screen to the debrief. Both flags must
  // flip together -- `showRoutineChoice` is checked before `showDebrief` in
  // the render below, so leaving it true (even after showDebrief becomes
  // true) would keep re-rendering this screen forever. Caught via live
  // testing: updateRoutine/createRoutine were both succeeding (confirmed in
  // the console) while the UI stayed stuck here, because this reset was
  // missing.
  const finishRoutineChoice = () => {
    setShowRoutineChoice(false);
    setShowDebrief(true);
  };

  const handleUpdateRoutine = async () => {
    if (!activeSession.routineId) {
      finishRoutineChoice();
      return;
    }
    setSavingRoutineChoice(true);
    setRoutineChoiceError(null);
    const result = await updateRoutine(activeSession.routineId, activeSession.routineName, buildMergedExercises());
    setSavingRoutineChoice(false);
    if (!result.success) {
      setRoutineChoiceError("Couldn't update the routine. Try again.");
      return;
    }
    finishRoutineChoice();
  };

  const handleSaveAsNewRoutine = async () => {
    setSavingRoutineChoice(true);
    setRoutineChoiceError(null);
    const result = await createRoutine(userId, `${activeSession.routineName} (Modified)`, buildMergedExercises());
    setSavingRoutineChoice(false);
    if (!result.success) {
      setRoutineChoiceError("Couldn't save the new routine. Try again.");
      return;
    }
    finishRoutineChoice();
  };

  const handleFinishMission = () => {
    if (addedExercises.length > 0 || removedExercises.length > 0 || orderChanged) {
      setShowRoutineChoice(true);
      return;
    }
    setShowDebrief(true);
  };

  if (showRoutineChoice) {
    const addedNames = addedExercises.map((ex) => ex.name).join(", ");
    const removedNames = removedExercises.map((ex) => ex.name).join(", ");
    const changeCount = addedExercises.length + removedExercises.length + (orderChanged ? 1 : 0);

    // Unified phrasing regardless of which change type(s) occurred, so
    // adding/removing/reordering (any combination) never has to fight over
    // a preposition ("added X to" vs "removed X from" vs "reordered...in").
    const changeParts = [];
    if (addedNames) changeParts.push(`added ${addedNames}`);
    if (removedNames) changeParts.push(`removed ${removedNames}`);
    if (orderChanged) changeParts.push("reordered the exercises");
    const changeSummary = changeParts.length <= 2
      ? changeParts.join(" and ")
      : `${changeParts.slice(0, -1).join(", ")}, and ${changeParts[changeParts.length - 1]}`;
    const changeDescription = `You made changes to "${activeSession.routineName}" this mission: ${changeSummary}.`;

    return (
      <div>
        <h2 className="comic-title" style={{ fontSize: "22px" }}>
          Keep {changeCount === 1 ? "This Change" : "These Changes"}?
        </h2>
        <p className="comic-subtitle">
          {changeDescription} What should we do with it?
        </p>

        <button
          type="button"
          onClick={handleUpdateRoutine}
          disabled={savingRoutineChoice || !activeSession.routineId}
          className="comic-btn comic-btn--go"
        >
          {savingRoutineChoice ? "Saving..." : `Update "${activeSession.routineName}"`}
        </button>
        <button
          type="button"
          onClick={handleSaveAsNewRoutine}
          disabled={savingRoutineChoice}
          className="comic-btn comic-btn--go"
        >
          {savingRoutineChoice ? "Saving..." : "Save as New Routine"}
        </button>
        <button
          type="button"
          onClick={finishRoutineChoice}
          disabled={savingRoutineChoice}
          className="comic-btn comic-btn--end"
        >
          Discard Changes
        </button>

        {routineChoiceError && <div className="comic-alert-bubble">{routineChoiceError}</div>}
      </div>
    );
  }

  // Rendered inside a Modal popup (below), overlaid on top of the still-live
  // Mission Active view, rather than replacing the whole screen — per direct
  // user request that the workout summary open as a popup the Hero closes
  // when they're done. Only exercises with at least one logged set make the
  // report — don't pad it with things the Hero didn't actually do this
  // mission. Uses allExercises (not just activeSession.exercises) so sets
  // logged against an on-the-fly addition still show up here.
  const renderDebrief = () => {
    const performed = allExercises
      .map((exercise) => ({ exercise, sets: (rows[exercise.exerciseId] || []).filter((r) => r.logged) }))
      .filter(({ sets }) => sets.length > 0);

    const totalSets = performed.reduce((sum, { sets }) => sum + sets.length, 0);
    const prLifts = performed.flatMap(({ exercise, sets }) =>
      sets.filter((set) => set.isPR).map((set) => ({ exercise, set }))
    );

    return (
      <>
        <span className="mission-stamp">Mission Complete</span>

        {totalSets === 0 ? (
          <p className="comic-empty" style={{ margin: "0 0 4px 0" }}>
            No sets logged this time. The gym will be here tomorrow, Hero.
          </p>
        ) : (
          <>
            {prLifts.length > 0 && (
              <div className="comic-panel comic-panel--entry">
                <div className="comic-title" style={{ fontSize: "18px", margin: 0 }}>
                  New PRs
                </div>
                {prLifts.map(({ exercise, set }, i) => (
                  <div key={`${exercise.exerciseId}-${i}`} className="stamp-row">
                    <span className="stamp-row__text">
                      🔥 {exercise.name}: {formatLogAmount(set)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {performed.map(({ exercise, sets }) => (
              <div key={exercise.exerciseId} className="comic-panel comic-panel--entry">
                <div className="comic-title" style={{ fontSize: "19px", margin: 0 }}>
                  {exercise.name}
                </div>
                <div className="comic-eyebrow" style={{ marginTop: "2px" }}>
                  {sets.length} set{sets.length > 1 ? "s" : ""} logged
                </div>
                <div>
                  {sets.map((set, i) => (
                    <div key={i} className="stamp-row">
                      <span className="stamp-row__badge stamp-row__badge--check">&#10003;</span>
                      <span className="stamp-row__text">
                        Set {i + 1}: {formatLogAmount(set)}
                      </span>
                      {set.isPR && <span className="pr-spark">🔥</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <p className="comic-subtitle" style={{ margin: "14px 0 0 0" }}>
              Total sets logged: {totalSets}
            </p>
          </>
        )}

        <button onClick={() => endSession(profileId)} className="comic-btn comic-btn--done">
          Done
        </button>
      </>
    );
  };

  const loggedCount = Object.values(rows).flat().filter((row) => row.logged).length;

  // Mission Complete / Cancel Mission row -- shared markup so the two
  // reproductions (top and bottom of the Mission Active section) can't
  // drift out of sync. Always visible now that the cancel confirmation
  // lives in its own Modal popup rather than replacing this row in place.
  const missionActionsRow = (
    <div style={{ display: "flex", gap: "10px" }} data-tour="mission-actions">
      <button
        onClick={handleFinishMission}
        className="comic-btn comic-btn--done"
        style={{ flex: 1 }}
      >
        Mission Complete
      </button>
      <button
        type="button"
        onClick={() => setConfirmingCancel(true)}
        className="comic-btn comic-btn--end"
        style={{ flex: 1 }}
      >
        Cancel Mission
      </button>
    </div>
  );

  return (
    <>
    <div>
      <h2 className="comic-title" style={{ fontSize: "22px", margin: 0 }}>
        In Progress
      </h2>
      <div
        className="comic-eyebrow"
        style={{ fontSize: "18px", padding: "3px 12px", border: "3px solid #232526", boxShadow: "3px 3px 0 0 #232526" }}
        data-tour="mission-header"
      >
        {activeSession.routineName}
      </div>

      {missionActionsRow}

      <div className="comic-speech-row">
        <div className="comic-avatar">
          {profile?.photoURL ? (
            <img src={profile.photoURL} alt="Hero" className="comic-avatar__img" />
          ) : (
            <svg viewBox="0 0 24 24" className="comic-avatar__icon" aria-hidden="true">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          )}
        </div>
        <div className="comic-speech-bubble">
          Tap the check when a set's done, Hero. Complete the mission when you're done.
        </div>
      </div>

      {allExercises.map((exercise, index) => {
        const exRows = rows[exercise.exerciseId] || [];
        const prevSets = previousSets[exercise.exerciseId] || [];
        const cardio = isCardioExercise(exercise);
        const isDragging = dragIndex === index;
        const isDropTarget = dragIndex !== null && dragOverIndex === index && !isDragging;

        return (
          <div
            key={exercise.exerciseId}
            ref={(el) => (cardRefs.current[index] = el)}
            className="comic-panel comic-panel--entry"
            style={{
              position: "relative",
              touchAction: isDragging ? "none" : undefined,
              transform: isDragging ? `translateY(${dragOffsetY}px)` : undefined,
              zIndex: isDragging ? 10 : undefined,
              background: isDragging ? "#fff8ea" : undefined,
              boxShadow: isDragging ? "3px 3px 0 0 #232526" : undefined,
              borderTop: isDropTarget ? "3px solid #00a274" : undefined
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", minWidth: 0 }}>
                <span
                  onPointerDown={(e) => handleDragPointerDown(e, index)}
                  onPointerMove={handleDragPointerMove}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                  aria-label={`Drag to reorder ${exercise.name}`}
                  role="button"
                  style={{
                    display: "inline-grid",
                    gridTemplateColumns: "repeat(2, 4px)",
                    gridTemplateRows: "repeat(3, 4px)",
                    gap: "3px",
                    flexShrink: 0,
                    padding: "10px 6px",
                    margin: "-2px 0 0 -6px",
                    touchAction: "none",
                    cursor: isDragging ? "grabbing" : "grab"
                  }}
                >
                  {Array.from({ length: 6 }).map((_, dotIndex) => (
                    <span
                      key={dotIndex}
                      aria-hidden="true"
                      style={{ width: "4px", height: "4px", borderRadius: "50%", background: "#232526" }}
                    />
                  ))}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="comic-title" style={{ fontSize: "20px", margin: 0 }}>
                    {exercise.name}
                  </div>
                  <div className="comic-eyebrow">
                    {exercise.targetMuscle || "Full Body"}{exercise.mainMuscle ? ` · ${exercise.mainMuscle}` : ""}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                <IconButton
                  icon="note"
                  onClick={() => setNoteExercise({ exerciseId: exercise.exerciseId, name: exercise.name })}
                  color={todayNotes[exercise.exerciseId] ? "#00a274" : "#232526"}
                  label={`${todayNotes[exercise.exerciseId] ? "Edit" : "Add"} note for ${exercise.name}`}
                  data-tour="exercise-note-btn"
                />
                <IconButton
                  icon="info"
                  onClick={() => setHistoryExercise({ exerciseId: exercise.exerciseId, name: exercise.name })}
                  color="#232526"
                  label={`View history for ${exercise.name}`}
                  data-tour="exercise-history-btn"
                />
                <IconButton
                  icon="delete"
                  onClick={() => setConfirmRemoveExercise(exercise)}
                  color="#232526"
                  label={`Remove ${exercise.name} from mission`}
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginTop: "12px",
                fontFamily: "'Anonymous Pro', monospace",
                fontSize: "11px",
                fontWeight: "bold",
                color: "#232526",
                opacity: 0.7
              }}
            >
              {/* No "Set" column for Cardio -- it's always exactly one row,
                  so a set number has nothing meaningful to count. */}
              {!cardio && <span style={{ width: "18px", textAlign: "center" }}>Set</span>}
              <span style={{ flex: 1 }}>Previous</span>
              {cardio ? (
                <>
                  <span style={{ width: "46px", textAlign: "center" }}>Hrs</span>
                  <span style={{ width: "46px", textAlign: "center" }}>Min</span>
                  <span style={{ width: "40px", textAlign: "center" }}>Dist</span>
                </>
              ) : (
                <>
                  <span style={{ width: "72px", textAlign: "center" }}>Weight (lb)</span>
                  <span style={{ width: "72px", textAlign: "center" }}>Reps</span>
                </>
              )}
              <span style={{ width: "30px", flexShrink: 0 }} />
            </div>

            {exRows.map((row, index) => {
              const prev = prevSets[index] ?? prevSets[prevSets.length - 1];
              const rowKey = `${exercise.exerciseId}:${index}`;
              const isSaving = savingRow === rowKey;
              return (
                <div key={index} style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "6px" }}>
                  {!cardio && (
                    <span
                      style={{
                        width: "18px",
                        textAlign: "center",
                        fontFamily: "'Anonymous Pro', monospace",
                        fontSize: "13px",
                        color: "#232526"
                      }}
                    >
                      {index + 1}
                    </span>
                  )}
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontFamily: "'Anonymous Pro', monospace",
                      fontSize: "12px",
                      fontWeight: "bold",
                      color: "#232526",
                      opacity: 0.6,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    {prev
                      ? (cardio ? formatCardioDurationShort(prev.durationMinutes, prev.distance) : formatLogAmount(prev))
                      : "—"}
                  </span>
                  {cardio ? (
                    <>
                      <AmountInput
                        value={row.hours}
                        onChange={(e) => handleFieldChange(exercise.exerciseId, index, "hours", e.target.value)}
                        onBlur={() => handleFieldBlur(exercise, index)}
                        unit=""
                        placeholder="0"
                        wrapperStyle={{ width: "46px", flexShrink: 0 }}
                        inputStyle={{ height: "36px", padding: "4px 4px", fontSize: "14px" }}
                      />
                      <AmountInput
                        value={row.minutes}
                        onChange={(e) => handleFieldChange(exercise.exerciseId, index, "minutes", e.target.value)}
                        onBlur={() => handleFieldBlur(exercise, index)}
                        unit=""
                        placeholder="0"
                        wrapperStyle={{ width: "46px", flexShrink: 0 }}
                        inputStyle={{ height: "36px", padding: "4px 4px", fontSize: "14px" }}
                      />
                      <AmountInput
                        value={row.distance}
                        onChange={(e) => handleFieldChange(exercise.exerciseId, index, "distance", e.target.value)}
                        onBlur={() => handleFieldBlur(exercise, index)}
                        unit=""
                        placeholder="—"
                        wrapperStyle={{ width: "40px", flexShrink: 0 }}
                        inputStyle={{ height: "36px", padding: "4px 2px", fontSize: "14px" }}
                      />
                    </>
                  ) : (
                    <>
                      <AmountInput
                        value={row.weight}
                        onChange={(e) => handleFieldChange(exercise.exerciseId, index, "weight", e.target.value)}
                        onBlur={() => handleFieldBlur(exercise, index)}
                        unit=""
                        placeholder="0"
                        wrapperStyle={{ width: "72px", flexShrink: 0 }}
                        inputStyle={{ height: "36px", padding: "4px 6px", fontSize: "14px" }}
                      />
                      <AmountInput
                        value={row.reps}
                        onChange={(e) => handleFieldChange(exercise.exerciseId, index, "reps", e.target.value)}
                        onBlur={() => handleFieldBlur(exercise, index)}
                        unit=""
                        placeholder="0"
                        wrapperStyle={{ width: "72px", flexShrink: 0 }}
                        inputStyle={{ height: "36px", padding: "4px 6px", fontSize: "14px" }}
                      />
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => (row.logged ? handleDeleteRow(exercise, index) : handleLogRow(exercise, index))}
                    disabled={isSaving}
                    aria-label={row.logged ? `Unmark set ${index + 1}` : `Mark set ${index + 1} done`}
                    style={{
                      width: "30px",
                      height: "30px",
                      borderRadius: "50%",
                      flexShrink: 0,
                      border: row.logged ? "2px solid #00a274" : "2px solid #232526",
                      background: row.logged ? "#00a274" : "transparent",
                      color: row.logged ? "#ffffff" : "#232526",
                      fontSize: "15px",
                      cursor: isSaving ? "default" : "pointer",
                      opacity: isSaving ? 0.6 : 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0
                    }}
                  >
                    {row.logged && row.isPR ? "🔥" : "✓"}
                  </button>
                </div>
              );
            })}

            {/* No "+ Add Set" for Cardio -- its row count is locked to 1. */}
            {!cardio && (
              <div style={{ marginTop: "10px" }}>
                <IconButton
                  icon="plus"
                  onClick={() => handleAddSet(exercise)}
                  color="#00a274"
                  label={`Add set to ${exercise.name}`}
                />
              </div>
            )}

            {bubble && bubble.exerciseId === exercise.exerciseId && (
              <div className="comic-alert-bubble">{bubble.message}</div>
            )}
          </div>
        );
      })}

      <div className="comic-panel comic-panel--entry">
        <button
          type="button"
          onClick={() => setShowAddExercise(true)}
          style={{
            background: "none",
            border: "none",
            color: "#fd874e",
            fontFamily: 'Anonymous Pro',
            fontWeight: "bold",
            fontSize: "14px",
            cursor: "pointer",
            textDecoration: "underline",
            padding: 0
          }}
        >
          + Add Exercise
        </button>
      </div>
      {showAddExercise && (
        <ExerciseSelectorModal
          exercises={availableExercises}
          userId={userId}
          tabKey="routines"
          title="Add Exercise"
          onSelect={(ex) => handleAddExerciseOnTheFly(ex)}
          onClose={() => setShowAddExercise(false)}
        />
      )}

      {missionActionsRow}
    </div>

    {showDebrief && (
      <Modal title={activeSession.routineName} tabKey="routines" onClose={() => endSession(profileId)}>
        {renderDebrief()}
      </Modal>
    )}

    {confirmRemoveExercise && (
      <ConfirmModal
        title="Remove Exercise?"
        message={`Remove "${confirmRemoveExercise.name}" from this mission? Any sets already logged for it this session will be deleted too.`}
        confirmLabel="Remove"
        confirming={removingExercise}
        onConfirm={confirmAndRemoveExercise}
        onCancel={() => setConfirmRemoveExercise(null)}
        tabKey="routines"
      />
    )}

    {noteExercise && (
      <ExerciseNoteModal
        exerciseId={noteExercise.exerciseId}
        exerciseName={noteExercise.name}
        dayKey={todayKey()}
        dayLabel="Today"
        userId={userId}
        tabKey="routines"
        onClose={() => setNoteExercise(null)}
        onSaved={(text) => setTodayNotes((prev) => ({ ...prev, [noteExercise.exerciseId]: text }))}
      />
    )}

    {historyExercise && (
      <ExerciseHistoryModal
        exerciseId={historyExercise.exerciseId}
        exerciseName={historyExercise.name}
        userId={userId}
        tabKey="routines"
        onClose={() => setHistoryExercise(null)}
      />
    )}

    {confirmingCancel && (
      <Modal
        title="Cancel Mission?"
        tabKey="routines"
        onClose={lockCancelConfirm ? () => {} : () => setConfirmingCancel(false)}
      >
        <p style={{ fontFamily: "'Anonymous Pro', monospace", fontSize: "14px", color: "#232526", margin: "0 0 14px 0" }}>
          Discard this mission? {loggedCount} set{loggedCount === 1 ? "" : "s"} logged so far will be deleted.
        </p>
        <div style={{ display: "flex", gap: "12px" }}>
          <button
            type="button"
            onClick={handleCancelMission}
            disabled={cancelling}
            className="comic-btn comic-btn--end"
            style={{ flex: 1 }}
            data-tour="cancel-mission-discard"
          >
            {cancelling ? "Discarding..." : "Discard"}
          </button>
          <button
            type="button"
            onClick={() => setConfirmingCancel(false)}
            disabled={cancelling || lockCancelConfirm}
            className="comic-btn comic-btn--done"
            style={{ flex: 1 }}
          >
            Keep Going
          </button>
        </div>
      </Modal>
    )}
    </>
  );
};

export default ActiveMission;
